import {
    DASHBOARD_QUERY_ROOT_KEY,
    DASHBOARD_SKIP_CACHE,
    DASHBOARD_STALE_TIME_MS,
} from "@/core/constants/dashboard.constants";
import { buildDashboardQueryKey } from "@/core/hooks/dashboardQueryKeys";
import { useDashboardStore } from "@/core/hooks/useDashboardStore";
import { dashboardQueryClient } from "@/core/plugins/vue-query";
import { DashboardService } from "@/core/services/Dashboard.service";
import {
  buildDefaultFilterDateRange,
  type FilterDateRange,
} from "@/core/helpers/filter-date-range";
import { useQuery } from "@tanstack/vue-query";
import { computed, watch } from "vue";
import { useStore } from "vuex";

/**
 * Loads dashboard meta and seeds Vuex with permissions, default scope, versions.
 * Re-applies when the authenticated user or tenant unit changes so id_don_vi stays in sync.
 */
export function useDashboardMeta() {
  const vuexStore = useStore();
  const {
    metaLoaded,
    scope,
    setScope,
    patchScope,
    setVersions,
    setPermissions,
    setMapConfig,
    setCapDonVi,
    setMetaLoaded,
    setSelectedMarkerId,
    resetDashboardState,
  } = useDashboardStore();

  const userId = computed(
    () =>
      (vuexStore.getters.currentUser?.id as number | null | undefined) ?? null
  );
  const departmentId = computed(
    () =>
      (vuexStore.getters.currentUser?.departmentId as
        | number
        | null
        | undefined) ?? null
  );
  const systemIdDonVi = computed(
    () =>
      (vuexStore.getters.getSystemIdDonVi as number | null | undefined) ?? null
  );

  /** Effective unit for scoped dashboard requests. */
  const effectiveIdDonVi = computed(
    () => systemIdDonVi.value ?? departmentId.value ?? null
  );

  const metaQueryKey = computed(() =>
    buildDashboardQueryKey("meta", null, {
      userId: userId.value,
      idDonVi: effectiveIdDonVi.value,
    })
  );

  const query = useQuery({
    queryKey: metaQueryKey,
    queryFn: () => DashboardService.getMeta(),
    staleTime: DASHBOARD_SKIP_CACHE ? 0 : DASHBOARD_STALE_TIME_MS.meta,
    gcTime: DASHBOARD_SKIP_CACHE ? 0 : undefined,
    enabled: computed(() => userId.value != null),
    shallow: true,
    throwOnError: false,
  });

  function buildDefaultDateRange(year: number): {
    fromDate: string;
    toDate: string;
  } {
    const stored = vuexStore.getters.getFilterDateRange as
      | FilterDateRange
      | null
      | undefined;

    if (stored && stored.year === year) {
      return {
        fromDate: stored.fromDate,
        toDate: stored.toDate,
      };
    }

    const fallback = buildDefaultFilterDateRange(year);
    return {
      fromDate: fallback.fromDate,
      toDate: fallback.toDate,
    };
  }

  function resolveScopeIdDonVi(
    defaultScopeIdDonVi: number | null | undefined
  ): number | undefined {
    if (defaultScopeIdDonVi != null) {
      return defaultScopeIdDonVi;
    }
    // Non-national scopes still need the current tenant unit on the query string.
    return effectiveIdDonVi.value ?? undefined;
  }

  function applyMeta(): void {
    const envelope = query.data.value;
    if (!envelope?.data || metaLoaded.value) {
      return;
    }

    const meta = envelope.data;
    setCapDonVi(meta.capDonVi);
    setPermissions(meta.permissions);
    setMapConfig(meta.map);
    setVersions(meta.versions);

    const year =
      Number(vuexStore.getters.getSelectedYear) || new Date().getFullYear();
    const { fromDate, toDate } = buildDefaultDateRange(year);

    const defaultScope = meta.defaultScope;
    const resolvedIdDonVi = resolveScopeIdDonVi(defaultScope.idDonVi);
    const shouldAttachIdDonVi =
      resolvedIdDonVi != null &&
      (defaultScope.type !== "national" || defaultScope.idDonVi != null);

    setScope({
      type: defaultScope.type,
      fromDate,
      toDate,
      ...(shouldAttachIdDonVi ? { idDonVi: resolvedIdDonVi } : {}),
      ...(defaultScope.idKho != null ? { idKho: defaultScope.idKho } : {}),
      ...(defaultScope.provinceCode
        ? { provinceCode: defaultScope.provinceCode }
        : {}),
    });
    setSelectedMarkerId(null);
    setMetaLoaded(true);
  }

  function clearDashboardQueries(): void {
    dashboardQueryClient.removeQueries({
      queryKey: [DASHBOARD_QUERY_ROOT_KEY],
    });
  }

  function resetForAuthContextChange(): void {
    resetDashboardState();
    clearDashboardQueries();
  }

  watch(
    () => query.data.value,
    () => {
      applyMeta();
    },
    { immediate: true }
  );

  // New login / different user: drop stale scope so id_don_vi is rebuilt from meta.
  watch(userId, (next, prev) => {
    if (prev == null || next == null || prev === next) {
      return;
    }
    resetForAuthContextChange();
  });

  // Tenant unit switch (system settings or user department): keep query param in sync.
  watch(effectiveIdDonVi, (next) => {
    if (!metaLoaded.value || !scope.value || next == null) {
      return;
    }

    if (scope.value.type === "national") {
      return;
    }

    if (scope.value.idDonVi !== next) {
      patchScope({ idDonVi: next });
    }
  });

  return {
    query,
    isLoading: computed(() => query.isLoading.value && !metaLoaded.value),
    isError: computed(() => query.isError.value && !metaLoaded.value),
    error: computed(() => query.error.value),
    meta: computed(() => query.data.value?.data ?? null),
    metaLoaded,
    scope,
    applyMeta,
    refetch: query.refetch,
    resetForAuthContextChange,
  };
}
